import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTopGenes, getAvailableSubtypes, getMetaAnalysis } from '../services/api';

const getSignificanceColor = (pval) => {
  if (!pval) return undefined;
  if (pval < 5e-8) return '#d32f2f';
  if (pval < 1e-6) return '#ed6c02';
  if (pval < 0.05) return '#0288d1';
  return undefined;
};

const formatPValue = (pval) => {
  if (pval === null || pval === undefined) return '';
  if (pval < 1e-10) return pval.toExponential(2);
  if (pval < 0.001) return pval.toExponential(2);
  return pval.toFixed(4);
};

const PvalCell = ({ value }) => {
  if (value === null || value === undefined) return '';
  const color = getSignificanceColor(value);
  return (
    <Box sx={{
      px: 1, py: 0.25, borderRadius: 1, display: 'inline-block',
      bgcolor: color ? `${color}22` : undefined,
      color: color || 'text.primary',
      fontWeight: color ? 600 : 400,
      fontSize: '0.8125rem',
    }}>
      {formatPValue(value)}
    </Box>
  );
};

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [disorder, setDisorder] = useState(searchParams.get('disorder') || 'asc');
  const [subtype, setSubtype] = useState(searchParams.get('subtype') || '');
  const [availableSubtypes, setAvailableSubtypes] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  const disordersWithSubtypes = ['bipex', 'epi25', 'cognition'];

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const urlDisorder = searchParams.get('disorder');
    const urlSubtype = searchParams.get('subtype');
    if (urlDisorder && urlDisorder !== disorder) setDisorder(urlDisorder);
    else if (!urlDisorder && disorder !== 'asc') setDisorder('asc');
    if (urlSubtype !== null && urlSubtype !== subtype) setSubtype(urlSubtype);
    else if (urlSubtype === null && subtype) setSubtype('');
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialMount.current) return;
    const currentDisorder = searchParams.get('disorder') || '';
    const currentSubtype = searchParams.get('subtype') || '';
    if (currentDisorder !== disorder || currentSubtype !== subtype) {
      const params = new URLSearchParams();
      if (disorder) params.set('disorder', disorder);
      if (subtype) params.set('subtype', subtype);
      setSearchParams(params, { replace: true });
    }
  }, [disorder, subtype, searchParams, setSearchParams]);

  useEffect(() => {
    if (disorder !== 'meta-analysis' && disordersWithSubtypes.includes(disorder)) {
      getAvailableSubtypes(disorder)
        .then(response => {
          setAvailableSubtypes(response.subtypes || []);
          setSubtype('');
        })
        .catch(() => setAvailableSubtypes([]));
    } else {
      setAvailableSubtypes([]);
      setSubtype('');
    }
  }, [disorder]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.title = `Browse ${disorder.toUpperCase()} Genes - Genetics Database`;
    const fetchData = async () => {
      setLoading(true);
      try {
        if (disorder === 'meta-analysis') {
          const response = await getMetaAnalysis(1000);
          if (response && Array.isArray(response.results)) {
            setData({
              disorder: 'meta-analysis',
              results: response.results.map((gene, idx) => ({
                id: gene.gene_id || idx,
                gene_id: gene.gene_id,
                symbol: gene.symbol,
                name: gene.name,
                location: gene.location,
                pval: gene.combined_pval,
                n_disorders: gene.n_disorders,
                disorders: gene.disorders,
              }))
            });
          } else {
            setData({ results: [] });
          }
        } else {
          const response = await getTopGenes(disorder, 1000, subtype || null);
          if (response && Array.isArray(response.results)) {
            setData({
              ...response,
              results: response.results.map((gene, idx) => ({ id: gene.gene_id || idx, ...gene }))
            });
          } else {
            setData({ results: [] });
          }
        }
      } catch (error) {
        console.error('Failed to load gene data');
        setData({ results: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [disorder, subtype]);

  const handleRowClick = (params) => {
    navigate(`/gene/${params.row.symbol}`);
  };

  // Build columns based on disorder
  const baseColumns = [
    { field: 'symbol', headerName: 'Symbol', width: 110, renderCell: (p) => (
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.value}</Typography>
    )},
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'location', headerName: 'Location', width: 120 },
  ];

  let columns;
  if (disorder === 'meta-analysis') {
    columns = [
      ...baseColumns,
      { field: 'n_disorders', headerName: 'N Disorders', width: 110, type: 'number' },
      { field: 'disorders', headerName: 'Disorders', width: 200,
        valueFormatter: (value) => Array.isArray(value) ? value.join(', ') : '' },
      { field: 'pval', headerName: 'Combined P-value', width: 150, type: 'number',
        renderCell: (p) => <PvalCell value={p.value} /> },
    ];
  } else {
    const pvalCol = { field: 'pval', headerName: 'P-value', width: 130, type: 'number',
      valueGetter: (value, row) => row.pval || row.p_meta || row.ptv_pval || row.denovowest_p_full,
      renderCell: (p) => <PvalCell value={p.value} /> };
    const qvalCol = { field: 'qval', headerName: 'Q-value', width: 130, type: 'number',
      valueGetter: (value, row) => row.qval ?? row.q_meta ?? null,
      renderCell: (p) => <PvalCell value={p.value} /> };
    const orCol = { field: 'or_display', headerName: 'OR', width: 100, type: 'number',
      valueGetter: (value, row) => row.ptv_or ?? row.or_ptv ?? row.or ?? null,
      valueFormatter: (value) => value != null ? Number(value).toFixed(2) : 'N/A' };
    const casesCol = { field: 'cases', headerName: 'Cases', width: 90, type: 'number',
      valueGetter: (value, row) => row.ptv_case_count ?? row.case_ptv ?? null };
    const controlsCol = { field: 'controls', headerName: 'Controls', width: 100, type: 'number',
      valueGetter: (value, row) => row.ptv_control_count ?? row.control_ptv ?? null };

    columns = [...baseColumns];
    if (disordersWithSubtypes.includes(disorder)) {
      columns.push({ field: 'subtype_display', headerName: 'Sub-trait', width: 150,
        valueGetter: (value, row) => row.subtype || row.group || row.phenotype || '' });
    }
    columns.push(pvalCol, qvalCol, orCol, casesCol, controlsCol);
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Browse Genes
      </Typography>

      {disorder === 'meta-analysis' && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Meta-Analysis (Fisher's Method):</strong> Combined analysis across all psychiatric disorders.
            Only includes one main phenotype per disorder to avoid over-representation.
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Disorder</InputLabel>
          <Select value={disorder} label="Disorder"
            onChange={(e) => { setDisorder(e.target.value); setSubtype(''); }}>
            <MenuItem value="asc">ASC (Autism)</MenuItem>
            <MenuItem value="schema">SCHEMA (Schizophrenia)</MenuItem>
            <MenuItem value="bipex">BipEx (Bipolar)</MenuItem>
            <MenuItem value="epi25">Epi25 (Epilepsy)</MenuItem>
            <MenuItem value="mdd">MDD (Depression)</MenuItem>
            <MenuItem value="ndd">NDD (Neurodevelopmental)</MenuItem>
            <MenuItem value="adhd">ADHD</MenuItem>
            <MenuItem value="cognition">Cognition</MenuItem>
            <MenuItem value="ocd">OCD</MenuItem>
            <MenuItem value="meta-analysis">Meta-Analysis (All Disorders)</MenuItem>
          </Select>
        </FormControl>

        {disorder !== 'meta-analysis' && disordersWithSubtypes.includes(disorder) && availableSubtypes.length > 0 && (
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Sub-trait / Subtype</InputLabel>
            <Select value={subtype} label="Sub-trait / Subtype"
              onChange={(e) => setSubtype(e.target.value)}>
              <MenuItem value="">All Sub-traits</MenuItem>
              {availableSubtypes.map((st) => (
                <MenuItem key={st.subtype} value={st.subtype}>
                  {st.subtype} ({st.count} genes)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : !data || !data.results || data.results.length === 0 ? (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No data available for {disorder.toUpperCase()}. The backend may be unreachable or no genes match the current filters.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={data.results}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'pval', sort: 'asc' }] },
            }}
            pageSizeOptions={[25, 50, 100]}
            onRowClick={handleRowClick}
            sx={{
              cursor: 'pointer',
              '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      )}
    </Container>
  );
};

export default BrowsePage;
